import { todayIn } from "@mapcraft-tasks/domain";
import { commandFingerprint, replicaFingerprint } from "./fingerprint";
import { applyCommand } from "./reducer";
import { applyEntities, cloneReplica, emptyReplica, type Replica, removeEntities } from "./replica";
import { memoryDriver, type PersistenceDriver, type Transport } from "./transport";
import type {
	Command,
	CommandLocal,
	CommandName,
	ConflictRecord,
	Entities,
	PullResult,
	QueuedOp,
	SyncStatus
} from "./types";

/**
 * Стор: `confirmed` + `pending` → `visible`.
 *
 * Из этой модели следует главное свойство интерфейса: **отката не существует**.
 * Отклонённая операция просто выпадает из `pending`, и `visible` пересчитывается без неё —
 * ни обратных команд, ни «сохранение не удалось, вернуть как было». Это устраняет самый
 * частый класс багов оптимистичных UI и делает задачей интерфейса не откат, а ЗАМЕТНОСТЬ
 * исчезновения (ui-spec Область 11).
 */

export type StoreOptions = {
	transport: Transport;
	persistence?: PersistenceDriver;
	now?: () => Date;
	/**
	 * Страховочный опрос. С поднятым SSE он нужен только на случай тихо оборвавшегося
	 * потока — отсюда и минуты, а не секунды.
	 */
	pollIntervalMs?: number;
	/**
	 * Источник сигнала «на сервере что-то изменилось» (SSE). Возвращает отписку.
	 * Стор не знает, откуда сигнал: ему важно только, что пора звать `pull`.
	 */
	events?: (onSync: () => void) => () => void;
};

export type StoreSnapshot = {
	replica: Replica;
	status: SyncStatus;
	conflicts: ConflictRecord[];
	tz: string;
	/** Растёт на каждый пересчёт — дешёвый ключ для `useSyncExternalStore`. */
	revision: number;
};

/** Контентные команды несут `baseContentVersion` — по нему сервер ловит расхождение текста. */
const CONTENT_COMMANDS = new Set<CommandName>(["renameNode", "setBody"]);

/** Схлопывание правок текста: набор в строке не должен порождать операцию на символ. */
const FOLD_WINDOW_MS = 500;

export class Store {
	private confirmed: Replica = emptyReplica();
	private visible: Replica = emptyReplica();
	private queue: QueuedOp[] = [];
	/**
	 * Принятые сервером, но ещё не приехавшие дельтой.
	 *
	 * Между ответом на `push` и ответом на `pull` изменение уже НЕ в очереди, а в
	 * `confirmed` ещё нет. Если в этот зазор пересчитать `visible`, строка на кадр-другой
	 * вернётся на старое место — и человек видит дребезг: применилось, откатилось,
	 * применилось. Поэтому принятая операция не выбрасывается, а переезжает сюда и
	 * продолжает накладываться поверх `confirmed` до первого удачного `pull`.
	 */
	private settled: QueuedOp[] = [];
	private conflicts: ConflictRecord[] = [];
	private cursor: string | null = null;
	private seq = 0;
	private revision = 0;
	private tz = "UTC";
	private status: SyncStatus = {
		online: true,
		pending: 0,
		conflicts: 0,
		lastSyncedAt: null,
		loading: true,
		hasReplica: false,
		error: null
	};
	/**
	 * Реплика известна — поднялась с диска или доехала пуллом. Держится полем, а не
	 * считается по `confirmed.nodes.size`: после удачного ПУСТОГО снапшота размер ноль,
	 * а реплика при этом самая настоящая.
	 */
	private replicaReady = false;
	/** Таймзона подтверждена сервером, а не поднята с диска. */
	private tzConfirmed = false;
	private settingsInflight: Promise<void> | null = null;
	/** Хвост очереди записей реплики: сохранения не должны идти внахлёст. */
	private persistChain: Promise<void> = Promise.resolve();

	private listeners = new Set<() => void>();
	private snapshot: StoreSnapshot;
	private inflight: Promise<void> | null = null;
	private drainRequested = false;
	private foldTimer: ReturnType<typeof setTimeout> | null = null;
	private timer: ReturnType<typeof setInterval> | null = null;
	private unsubscribeEvents: (() => void) | null = null;
	private isLeader = false;
	private releaseLeadership: (() => void) | null = null;
	private abortLeadership: AbortController | null = null;
	private peers: BroadcastChannel | null = null;
	private readonly persistence: PersistenceDriver;
	private readonly now: () => Date;

	constructor(private readonly options: StoreOptions) {
		this.persistence = options.persistence ?? memoryDriver();
		this.now = options.now ?? (() => new Date());
		this.snapshot = this.buildSnapshot();
	}

	// --- контракт useSyncExternalStore ------------------------------------

	subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	};

	getSnapshot = (): StoreSnapshot => this.snapshot;

	// --- жизненный цикл ----------------------------------------------------

	/**
	 * Старт НЕ ждёт сети ни в одной точке. Данные лежат на диске, и ждать сервер, чтобы
	 * показать своё же дерево, незачем: именно ожидание сети на старте и давало в PWA без
	 * связи пустой экран цвета фона.
	 */
	async start(): Promise<void> {
		const persisted = await this.persistence.load();
		if (persisted) {
			// Очередь и карантин восстанавливаются ВСЕГДА: они невосстановимы, это работа
			// человека оффлайн. Реплика — только если её форма совпала с текущей.
			this.queue = persisted.queue;
			this.conflicts = persisted.conflicts;
			this.seq = persisted.queue.reduce((max, op) => Math.max(max, op.seq), 0);

			if (persisted.meta.replicaFingerprint === replicaFingerprint()) {
				this.cursor = persisted.meta.cursor;
				// Таймзона — ДО `recompute()`: «сегодня» считается по ней, и посчитать его
				// сперва по UTC значило бы на кадр показать чужой день.
				this.tz = persisted.meta.tz ?? this.tz;
				applyEntities(this.confirmed, persisted.entities);
				// Даже если сущностей ноль: снапшот когда-то доехал, это не «мы ничего не знаем».
				this.replicaReady = true;
				this.setStatus({ loading: false });
			} else {
				// Форма реплики поменялась — снести и взять полный снапшот. Бампать
				// руками нечего, забыть невозможно (offline-sync §2.1).
				console.log("[store] форма реплики изменилась — беру снапшот заново");
				await this.persistence.clearReplica();
			}
			this.recompute();
		}

		// Лидер среди вкладок: очередь толкает и SSE держит ОДНА. Остальные читают стор
		// и пишут в очередь — иначе три вкладки трижды отправили бы одну операцию
		// (offline-sync §12).
		this.claimLeadership();
		this.listenToPeers();

		await this.sync();
		// Первая попытка ЗАВЕРШЕНА, каким бы ни был исход (`runSync` не бросает): дальше
		// показывать скелет нечестно — либо есть что показать, либо связи нет.
		this.setStatus({ loading: false });

		// Порядок «сначала push, потом pull» задан протоколом: свои операции, потом чужие
		// изменения вместе с результатом своих (offline-sync §7).
		// Онлайн-инвалидация и оффлайн-догон — ОДИН И ТОТ ЖЕ код, отличается только пауза.
		this.subscribeEvents();

		const interval = this.options.pollIntervalMs ?? (this.unsubscribeEvents ? 120_000 : 15_000);
		this.timer = setInterval(() => void this.sync(), interval);

		if (typeof window !== "undefined") {
			window.addEventListener("online", this.onOnline);
			window.addEventListener("offline", this.onOffline);
			this.setStatus({ online: navigator.onLine });
		}
	}

	/**
	 * Идемпотентно: прежний поток закрываем сами. Иначе ссылка на него теряется навсегда —
	 * а такой порядок вполне достижим, старт больше не блокируется сетью, и человек успевает
	 * войти (и дёрнуть `resubscribe`) прямо посреди `start()`.
	 */
	private subscribeEvents(): void {
		this.unsubscribeEvents?.();
		this.unsubscribeEvents = this.options.events?.(() => void this.sync()) ?? null;
	}

	/**
	 * Переподписаться на события и догнаться. Нужен после повторного входа: `EventSource`,
	 * получивший не-200 (а 401 — это он), закрыт НАВСЕГДА — в отличие от обрыва сети, где
	 * он переподключается сам.
	 */
	resubscribe(): void {
		this.subscribeEvents();
		void this.sync();
	}

	stop(): void {
		this.clearFoldTimer();
		this.unsubscribeEvents?.();
		this.unsubscribeEvents = null;
		this.releaseLeadership?.();
		this.releaseLeadership = null;
		this.abortLeadership?.abort();
		this.abortLeadership = null;
		this.peers?.close();
		this.peers = null;
		if (this.timer !== null) {
			clearInterval(this.timer);
			this.timer = null;
		}
		if (typeof window !== "undefined") {
			window.removeEventListener("online", this.onOnline);
			window.removeEventListener("offline", this.onOffline);
		}
	}

	private onOnline = () => {
		this.setStatus({ online: true });
		void this.sync();
	};

	private onOffline = () => {
		this.setStatus({ online: false });
	};

	// --- запись ------------------------------------------------------------

	/**
	 * Единственная дверь записи. Синхронна и НЕ возвращает промис: UI не ждёт сервер
	 * никогда — отсюда отсутствие спиннеров, `disabled` на время сохранения и тостов
	 * «сохранено» (ui-spec Область 1).
	 */
	dispatch = (command: Command, local?: CommandLocal): void => {
		const now = this.now();
		const folded = this.tryFold(command, now);
		if (!folded) {
			this.seq += 1;
			this.queue.push({
				opId: crypto.randomUUID(),
				seq: this.seq,
				name: command.name,
				input: command.input,
				local,
				// Отпечаток схемы НА МОМЕНТ ПОСТАНОВКИ: при дрейне сверяется с текущим,
				// и в карантин уходят только записи этого типа, а не вся очередь.
				fingerprint: commandFingerprint(command.name),
				queuedAt: now.getTime(),
				sentAt: null
			});
		}
		this.recompute();
		void this.persistQueue();
		this.notifyPeers("queued");
		this.scheduleDrain(command);
	};

	/**
	 * Схлопывание (fold): последовательные правки ОДНОГО поля ОДНОЙ ноды, ещё не ушедшие
	 * на сервер, складываются в одну операцию. Ключ идемпотентности при этом сохраняется —
	 * иначе каждый символ стал бы отдельной записью очереди.
	 */
	private tryFold(command: Command, now: Date): boolean {
		if (!CONTENT_COMMANDS.has(command.name)) {
			return false;
		}
		const last = this.queue[this.queue.length - 1];
		if (!last || last.name !== command.name || last.sentAt !== null) {
			return false;
		}
		if (now.getTime() - last.queuedAt > FOLD_WINDOW_MS) {
			return false;
		}
		const prevInput = last.input as { nodeId: string };
		const nextInput = command.input as { nodeId: string; baseContentVersion: number };
		if (prevInput.nodeId !== nextInput.nodeId) {
			return false;
		}
		// База берётся от ПЕРВОЙ правки: она и есть та ревизия, которую человек видел.
		const base = (last.input as { baseContentVersion: number }).baseContentVersion;
		last.input = { ...nextInput, baseContentVersion: base };
		last.queuedAt = now.getTime();
		return true;
	}

	/** Ревизия контента, которую видел человек, — из `confirmed`, а не из `visible`. */
	contentVersionOf(nodeId: string): number {
		return this.confirmed.nodes.get(nodeId)?.contentVersion ?? 1;
	}

	today(): string {
		return todayIn(this.tz, this.now());
	}

	/**
	 * «Применить заново» — НОВАЯ операция, а не пере-отправка записи очереди.
	 *
	 * Тот же `opId` бессмысленен по построению: ключ занят записью `rejected`, и обёртка
	 * вернёт её же — операция останется отвергнутой навсегда, сколько ни жми. Поэтому
	 * интент пересобирается против ТЕКУЩЕГО `confirmed`: новый `opId`, заново взятый
	 * `baseContentVersion` (offline-sync §7.4).
	 */
	retryConflict(opId: string): void {
		const conflict = this.conflicts.find((c) => c.opId === opId);
		if (!conflict) {
			return;
		}
		const input = conflict.input as Record<string, unknown>;
		const rebuilt =
			typeof input.baseContentVersion === "number" && typeof input.nodeId === "string"
				? { ...input, baseContentVersion: this.contentVersionOf(input.nodeId) }
				: input;

		this.discardConflict(opId);
		this.dispatch({ name: conflict.name, input: rebuilt } as Command);
	}

	/**
	 * Причина отказа всё ещё воспроизводится локально? Тогда «применить заново» вернёт
	 * тот же отказ, и кнопку надо гасить, а не давать бить в стену (ui-spec Область 11).
	 * Проверка дешёвая и намеренно узкая: цель операции обязана существовать и быть живой.
	 */
	canRetry(opId: string): boolean {
		const conflict = this.conflicts.find((c) => c.opId === opId);
		if (!conflict) {
			return false;
		}
		return conflict.nodeIds.every((id) => {
			const node = this.confirmed.nodes.get(id);
			return node !== undefined && node.archivedAt === null;
		});
	}

	discardConflict(opId: string): void {
		this.conflicts = this.conflicts.filter((c) => c.opId !== opId);
		void this.persistConflicts();
		this.setStatus({ conflicts: this.conflicts.length });
	}

	// --- синхронизация -----------------------------------------------------

	/**
	 * **Структурные операции уходят СРАЗУ, контентные — с дебаунсом.**
	 *
	 * Без этого схлопывание правок было бы декоративным: каждый символ немедленно
	 * улетал бы на сервер, следующая правка уже не могла бы сложиться с отправленной,
	 * и «одна операция на слово» превращалась бы в «операция на букву» (offline-sync §4).
	 * Структуру дерева, наоборот, откладывать нельзя — она меняет форму документа.
	 */
	private scheduleDrain(command: Command): void {
		this.clearFoldTimer();
		if (CONTENT_COMMANDS.has(command.name)) {
			this.foldTimer = setTimeout(() => {
				this.foldTimer = null;
				void this.sync();
			}, FOLD_WINDOW_MS);
			return;
		}
		void this.sync();
	}

	private clearFoldTimer(): void {
		if (this.foldTimer !== null) {
			clearTimeout(this.foldTimer);
			this.foldTimer = null;
		}
	}

	/** Принудительная отправка: блюр строки и уход со страницы не ждут дебаунса. */
	flush(): Promise<void> {
		this.clearFoldTimer();
		return this.sync();
	}

	/**
	 * Возвращает промис ТЕКУЩЕГО цикла, а не пустой: вызов во время работы обязан
	 * дождаться результата, иначе `await sync()` соврал бы — вернулся до отправки
	 * только что поставленной операции.
	 */
	sync(): Promise<void> {
		this.clearFoldTimer();
		if (this.inflight) {
			this.drainRequested = true;
			return this.inflight;
		}
		this.inflight = this.runSync().finally(() => {
			this.inflight = null;
		});
		return this.inflight;
	}

	private async runSync(): Promise<void> {
		try {
			do {
				this.drainRequested = false;
				await this.drainQueue();
				await this.pull();
			} while (this.drainRequested);
			this.setStatus({ error: null, online: true });
			// Без `await`: `start()` ждёт `sync()`, и любое ожидание настроек ЗДЕСЬ держало бы
			// старт ровно так же, как прежний `await settings()` в начале `start()`.
			this.requestSettings();
		} catch (e) {
			this.setStatus({ error: e instanceof Error ? e.message : String(e), online: false });
		}
	}

	/**
	 * Настройки — ДОВЕСОК к синку, а не его часть: без них приложение работает (tz поднят
	 * с диска), а ждать их — значит держать старт. Отсюда и вызов без ожидания, и
	 * дедупликация: зависший запрос не должен порождать по новому на каждый тик.
	 */
	private requestSettings(): void {
		if (this.tzConfirmed || this.settingsInflight) {
			return;
		}
		this.settingsInflight = this.loadSettings().finally(() => {
			this.settingsInflight = null;
		});
	}

	private async loadSettings(): Promise<void> {
		// try накрывает и запись меты: этот промис никто не ждёт, и любая ошибка отсюда
		// стала бы unhandled rejection.
		try {
			this.tz = (await this.options.transport.settings()).tz;
			this.tzConfirmed = true;
			this.recompute(); // «сегодня» могло смениться — производные пересчитать
			if (this.replicaReady) {
				// Не отдельная запись меты, а обычный `persistReplica`. Отдельная взяла бы
				// ТЕКУЩИЙ курсор, а сущности на диске к этому моменту могли быть ещё от
				// прошлого пулла — курсор уехал бы вперёд реплики, и следующий старт молча
				// пропустил бы дельту навсегда. Здесь же сущности и курсор кладутся одной
				// согласованной парой. Без записи вовсе tz попала бы на диск только со
				// следующим пуллом, и первый оффлайн-старт после установки считал бы
				// «сегодня» по UTC.
				await this.persistReplica();
			}
		} catch {
			// Молча, но с правом на повтор: упасть могла и запись меты уже ПОСЛЕ
			// `tzConfirmed = true`, и тогда без сброса следующий синк не стал бы
			// переспрашивать — tz жила бы только в памяти и не пережила перезапуск.
			this.tzConfirmed = false;
		}
	}

	private async drainQueue(): Promise<void> {
		if (this.queue.length === 0) {
			return;
		}
		if (!this.isLeader) {
			// Толкает очередь ровно одна вкладка. Остальные уже разбудили её `notifyPeers`.
			return;
		}

		this.quarantineStaleOps();
		if (this.queue.length === 0) {
			return;
		}
		const batch = [...this.queue];
		const sentAt = this.now().getTime();
		for (const op of batch) {
			op.sentAt = sentAt;
		}

		// `local` на провод не уходит: это подпорка редьюсера, а не часть намерения.
		// Сервер восстанавливает по СВОЕМУ слепку, который полнее нашего.
		const results = await this.options.transport.push(batch.map(({ local: _local, ...op }) => op));
		const byOp = new Map(results.map((r) => [r.opId, r]));

		for (const op of batch) {
			const result = byOp.get(op.opId);
			if (!result) {
				// Ответа по операции нет — оставляем в очереди, повтор пойдёт с ТЕМ ЖЕ opId
				// (транспортный ретрай), и сервер вернёт сохранённый результат.
				op.sentAt = null;
				continue;
			}
			if (result.status === "rejected") {
				// Отклонённая исчезает совсем: её эффекта на сервере нет, и держать его
				// на экране до `pull` значило бы показывать несуществующее.
				this.quarantine(op, result.code ?? "conflict", result.message ?? "операция отклонена");
			} else {
				this.settled.push(op);
			}
			this.queue = this.queue.filter((q) => q.opId !== op.opId);
		}

		this.recompute();
		await this.persistQueue();
	}

	private quarantine(op: QueuedOp, code: string, message: string): void {
		this.conflicts.push({
			opId: op.opId,
			kind: code === "stale_content" ? "content" : "structure",
			name: op.name,
			input: op.input,
			code,
			message,
			at: this.now().getTime(),
			nodeIds: affectedNodeIds(op)
		});
		void this.persistConflicts();
		this.setStatus({ conflicts: this.conflicts.length });
	}

	private async pull(): Promise<void> {
		const result: PullResult = await this.options.transport.pull(this.cursor);

		if (result.kind === "snapshot") {
			// Протухший курсор ⇒ `confirmed` перестраивается целиком, а неотправленная
			// очередь формировалась против состояния, которое мы уже не можем соотнести
			// с текущим. Применять её вслепую нельзя — она уходит в карантин (§7.3).
			if (this.cursor !== null && this.queue.length > 0) {
				for (const op of this.queue) {
					this.quarantine(op, "conflict", "курсор устарел — операция не могла быть применена вслепую");
				}
				this.queue = [];
			}
			this.confirmed = emptyReplica();
			applyEntities(this.confirmed, result.snapshot);
		} else {
			applyEntities(this.confirmed, result.changed);
			removeEntities(this.confirmed, result.removed);
		}

		this.cursor = result.cursor;
		// Дельта считалась ПОСЛЕ того, как сервер подтвердил push, значит эффекты принятых
		// операций в ней уже есть — накладывать их вторым слоем больше не нужно. Если бы
		// `pull` упал, сброса не случилось бы и картинка осталась целой до следующего цикла.
		this.settled = [];
		// Любой удачный пулл — снапшот, дельта, пустая дельта — делает реплику известной.
		this.replicaReady = true;
		this.setStatus({ lastSyncedAt: this.now() });
		this.recompute();
		await this.persistReplica();
		this.notifyPeers("synced");
	}

	// --- мультитаб ---------------------------------------------------------

	/**
	 * Лидер выбирается через Web Locks: держатель лока и есть та вкладка, что толкает
	 * очередь. Лок берётся навсегда и освобождается при закрытии вкладки — браузер
	 * сам передаст его следующему претенденту.
	 *
	 * Нет Web Locks (старый браузер) — считаем лидером себя: одна вкладка это норма,
	 * а без выбора лидера приложение просто обязано работать.
	 */
	private claimLeadership(): void {
		if (typeof navigator === "undefined" || !navigator.locks) {
			this.isLeader = true;
			return;
		}
		// Запрос лока обязан умирать вместе со стором. Иначе закрытая вкладка остаётся в
		// очереди претендентов и однажды получает лидерство уже мёртвой — а лок она держит
		// вечно, и очередь не толкает больше никто.
		this.abortLeadership = new AbortController();
		navigator.locks
			.request("mapcraft-sync-leader", { signal: this.abortLeadership.signal }, () => {
				this.isLeader = true;
				void this.sync();
				return new Promise<void>((resolve) => {
					this.releaseLeadership = () => {
						this.isLeader = false;
						resolve();
					};
				});
			})
			.catch(() => {
				// Отменённый запрос — это наш же `stop()`, а не сбой.
			});
	}

	/**
	 * Между вкладками ходит только СИГНАЛ, а не состояние: «в очередь положили» будит
	 * лидера, «синхронизировались» велит остальным перечитать реплику из IndexedDB.
	 * Гонять сами данные значило бы завести третий канал доставки рядом с дельтой и IDB.
	 */
	private listenToPeers(): void {
		if (typeof BroadcastChannel === "undefined") {
			return;
		}
		this.peers = new BroadcastChannel("mapcraft-store");
		this.peers.onmessage = (event: MessageEvent<"queued" | "synced">) => {
			if (event.data === "queued" && this.isLeader) {
				void this.reloadQueue().then(() => this.sync());
			}
			if (event.data === "synced" && !this.isLeader) {
				void this.reloadFromDisk();
			}
		};
	}

	private notifyPeers(message: "queued" | "synced"): void {
		this.peers?.postMessage(message);
	}

	private async reloadQueue(): Promise<void> {
		const persisted = await this.persistence.load();
		if (persisted) {
			this.queue = persisted.queue;
			this.seq = persisted.queue.reduce((max, op) => Math.max(max, op.seq), this.seq);
			this.recompute();
		}
	}

	private async reloadFromDisk(): Promise<void> {
		const persisted = await this.persistence.load();
		if (!persisted || persisted.meta.replicaFingerprint !== replicaFingerprint()) {
			return;
		}
		this.confirmed = emptyReplica();
		applyEntities(this.confirmed, persisted.entities);
		this.cursor = persisted.meta.cursor;
		// И tz тоже: без неё вкладка-нелидер считала бы «сегодня» по UTC.
		this.tz = persisted.meta.tz ?? this.tz;
		this.queue = persisted.queue;
		this.conflicts = persisted.conflicts;
		this.replicaReady = true;
		this.recompute();
	}

	/**
	 * Граница окна ретеншна ключей — это граница ПРАВА на транспортный ретрай.
	 *
	 * Операция, которая уже уходила, но ответа не получила, и которой больше девяноста
	 * дней, повтором не лечится: ключ на сервере вычищен, и повтор применит её ВТОРОЙ раз.
	 * Это ровно тот случай «применилось или нет — неизвестно», и решается он человеком.
	 * Здесь же ловится и рассинхрон формы команды (offline-sync §2.1в, §7.1).
	 */
	private quarantineStaleOps(): void {
		const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
		const now = this.now().getTime();
		const survivors: QueuedOp[] = [];

		for (const op of this.queue) {
			if (op.fingerprint !== commandFingerprint(op.name)) {
				this.quarantine(op, "invalid_input", `формат команды «${op.name}» изменился с момента постановки`);
				continue;
			}
			if (op.sentAt !== null && now - op.queuedAt > RETENTION_MS) {
				this.quarantine(
					op,
					"conflict",
					"операция отправлялась больше 90 дней назад — применилась она или нет, неизвестно"
				);
				continue;
			}
			survivors.push(op);
		}

		if (survivors.length !== this.queue.length) {
			this.queue = survivors;
			void this.persistQueue();
			this.recompute();
		}
	}

	/**
	 * Очередь и карантин пишутся СРАЗУ — они невосстановимы. Реплика придерживается:
	 * писать двадцать тысяч нод на каждое нажатие клавиши незачем, она восстанавливается
	 * одним `pull`.
	 */
	private async persistQueue(): Promise<void> {
		await this.persistence.saveQueue(this.queue);
	}

	private async persistConflicts(): Promise<void> {
		await this.persistence.saveConflicts(this.conflicts);
	}

	/**
	 * Реплика и мета — ОДНА запись, и записи выстроены в очередь.
	 *
	 * Инвариант простой: курсор на диске никогда не опережает сущности. Два сохранения
	 * внахлёст его ломают — сущности одного пулла, мета другого, — и следующий старт пойдёт
	 * за дельтой от курсора, изменений до которого в локальной реплике нет. Это потеря
	 * навсегда: дельта их больше не принесёт.
	 */
	private persistReplica(): Promise<void> {
		const task = this.persistChain.then(async () => {
			// Слепок снимается в момент записи, а не постановки в очередь: пара
			// «сущности + курсор» обязана быть из одного состояния памяти.
			const entities = toEntities(this.confirmed);
			const meta = { cursor: this.cursor, replicaFingerprint: replicaFingerprint(), tz: this.tz };
			await this.persistence.saveEntities(entities);
			await this.persistence.saveMeta(meta);
		});
		// Цепочку отказ не должен заклинивать: ошибку получает вызвавший, очередь живёт дальше.
		this.persistChain = task.catch(() => {});
		return task;
	}

	// --- пересчёт ----------------------------------------------------------

	private recompute(): void {
		const next = cloneReplica(this.confirmed);
		const today = this.today();
		// `settled` заведомо старше `queue` — обе половины уже в порядке постановки,
		// сортировать нечего.
		for (const op of [...this.settled, ...this.queue]) {
			applyCommand(next, { name: op.name, input: op.input } as Command, {
				opId: op.opId,
				now: new Date(op.queuedAt),
				today,
				local: op.local
			});
		}
		this.visible = next;
		this.status = {
			...this.status,
			pending: this.queue.length,
			conflicts: this.conflicts.length,
			hasReplica: this.replicaReady
		};
		this.emit();
	}

	private setStatus(patch: Partial<SyncStatus>): void {
		this.status = { ...this.status, ...patch };
		this.emit();
	}

	private buildSnapshot(): StoreSnapshot {
		return {
			replica: this.visible,
			status: this.status,
			conflicts: this.conflicts,
			tz: this.tz,
			revision: this.revision
		};
	}

	private emit(): void {
		this.revision += 1;
		this.snapshot = this.buildSnapshot();
		for (const listener of this.listeners) {
			listener();
		}
	}
}

/** Реплика → плоские массивы для записи в IndexedDB. */
function toEntities(replica: Replica): Entities {
	return {
		nodes: [...replica.nodes.values()],
		placements: [...replica.placements.values()],
		links: [...replica.links.values()],
		completions: [...replica.completions.values()],
		tags: [...replica.tags.values()]
	};
}

/** Множество затронутых id: по ним рисуется инлайн-маркер и «отравляется» хвост очереди. */
function affectedNodeIds(op: QueuedOp): string[] {
	const input = op.input as Record<string, unknown>;
	const ids: string[] = [];
	if (typeof input.nodeId === "string") {
		ids.push(input.nodeId);
	}
	if (typeof input.id === "string") {
		ids.push(input.id);
	}
	if (Array.isArray(input.nodeIds)) {
		ids.push(...(input.nodeIds as string[]));
	}
	if (typeof input.parentId === "string") {
		ids.push(input.parentId);
	}
	if (typeof input.containerId === "string") {
		ids.push(input.containerId);
	}
	return [...new Set(ids)];
}
